import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { redirect } from 'redux-first-router'
import { useDeleteJobModal } from 'components/modals/DeleteJobModal/hooks'
import { mapInternalStateToPayload as mapJobPositionStateToPayload } from 'components/modals/common/JobPositionModal/actions'
import { getLanguage, getUser } from 'ducks/auth'
import { useRole } from 'components/auth/features'
import { useModal, useSnackbar } from 'hooks'
import { ACTIONS, getActions } from './model'
import { DEFAULT_JOBS_QUERY_PARAMS } from 'constants/projects'
import { jobTemplates, jobs, interviewTemplates } from 'orm'
import { toProjects } from 'ducks/router'
import { getDefaultTemplate } from 'components/pages/Jobs/JobListing/hooks'
import { t } from 'ttag'
import { INTERVIEW_MOUDLE_FIELD } from 'components/modals/common/JobPositionModal/tabs/Modules/model'

const useJobDetailsActions = handlers => {
  const { handleDeleteJobModalOpen } = handlers

  const actions = React.useMemo(
    () =>
      getActions().map(action => {
        const mapTypeToOnClick = {
          [ACTIONS.DELETE]: handleDeleteJobModalOpen,
        }

        return {
          ...action,
          onClick: mapTypeToOnClick[action.type],
        }
      }),
    [handleDeleteJobModalOpen],
  )

  return {
    state: { actions },
  }
}

const useCreateTemplateModal = () => {
  const dispatch = useDispatch()

  const { isOpen, handleClose, handleOpen } = useModal()

  const handleConfirm = React.useCallback(
    (data, { onFailureAction }: { onFailureAction?: () => void } = {}) => {
      dispatch(
        jobTemplates.postStart({
          data,
          onFailureAction,
          postSuccessAction: () => {
            handleClose()
          },
        }),
      )
    },
    [dispatch, handleClose],
  )

  return {
    handleCreateTemplateModalClose: handleClose,
    handleCreateTemplateModalConfirm: handleConfirm,
    handleCreateTemplateModalOpen: handleOpen,
    isCreateTemplateModalOpen: isOpen,
  }
}

const useEditPositionModal = state => {
  const { id } = state

  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const isAccountAdmin = useRole('accountAdmin')

  const { isOpen, handleClose, handleOpen } = useModal()

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const handleConfirm = React.useCallback(
    async (data, { onError, onSuccess }) => {
      const payload = mapJobPositionStateToPayload(data)

      const templateValue = data?.template?.value
      const templatePosition = data?.position
      const hasInterviewModule = !!data?.modules.find(
        module => module.name === INTERVIEW_MOUDLE_FIELD,
      )[INTERVIEW_MOUDLE_FIELD]

      const isCurrentUserAccessRevoked =
        !isAccountAdmin && !payload.members_with_access.includes(user.pk)

      const postJob = (extendedPayload?: Record<string, unknown>) => {
        dispatch(
          jobs.patchStart({
            pk: id,
            noSnackbar: true,
            data: { ...payload, ...extendedPayload },
            onFailureAction: onError,
            onSuccessAction: !isCurrentUserAccessRevoked
              ? jobs.fetchStart({
                  pk: id,
                })
              : undefined,
            postSuccessAction: () => {
              handleClose()
              if (onSuccess) {
                onSuccess()
              }

              handleShowSnackbar({
                message: t`Job was successfully edited.`,
                type: 'success',
              })

              if (isCurrentUserAccessRevoked) {
                dispatch(
                  redirect(toProjects(null, null, DEFAULT_JOBS_QUERY_PARAMS)),
                )

                handleShowSnackbar({
                  message: t`You have revoked your permission to access ${payload.name}`,
                  type: 'info',
                  timeout: 10000,
                })
              }
            },
          }),
        )
      }

      // if default template was selected by the user
      // then create it and afterwards create a job
      if (hasInterviewModule && !templateValue) {
        dispatch(
          interviewTemplates.postStart({
            data: getDefaultTemplate(data, templatePosition),
            noSnackbar: true,
            postSuccessAction: () =>
              postJob({
                interview_template: `${templatePosition} ${t`Template`}`,
              }),
          }),
        )
      } else {
        postJob()
      }
    },
    [id, dispatch, handleClose, user, isAccountAdmin, handleShowSnackbar],
  )

  return {
    handleEditPositionModalClose: handleClose,
    handleEditPositionModalConfirm: handleConfirm,
    handleEditPositionModalOpen: handleOpen,
    isEditPositionModalOpen: isOpen,
  }
}

export const useJobDetailsTab = state => {
  const { id } = state

  const language = useSelector(getLanguage)

  const {
    handleEditPositionModalClose,
    handleEditPositionModalConfirm,
    handleEditPositionModalOpen,
    isEditPositionModalOpen,
  } = useEditPositionModal({ id })

  const {
    handleDeleteJobModalClose,
    handleDeleteJobModalConfirm,
    handleDeleteJobModalOpen,
    isDeleteJobModalOpen,
  } = useDeleteJobModal({ id })

  const {
    handleCreateTemplateModalClose,
    handleCreateTemplateModalConfirm,
    handleCreateTemplateModalOpen,
    isCreateTemplateModalOpen,
  } = useCreateTemplateModal()

  const {
    state: { actions },
  } = useJobDetailsActions({ handleDeleteJobModalOpen })

  jobTemplates.useModel()

  return {
    state: {
      actions,
      language,
      isDeleteJobModalOpen,
      isEditPositionModalOpen,
      isCreateTemplateModalOpen,
    },
    handlers: {
      handleDeleteJobModalClose,
      handleDeleteJobModalConfirm,
      handleEditPositionModalClose,
      handleEditPositionModalConfirm,
      handleEditPositionModalOpen,
      handleCreateTemplateModalClose,
      handleCreateTemplateModalConfirm,
      handleCreateTemplateModalOpen,
    },
  }
}
